import React from "react";
import Stack from "@mui/material/Stack";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Typography from "@mui/material/Typography";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import WarningIcon from '@mui/icons-material/Warning';

import {formatPriceCent} from "./index";
import styled from "@emotion/styled";
import IconButton from "@mui/material/IconButton";
import {CircleCssIcon, PriceTag, StatusTag, PeopleTag} from "./plannerTag";

// Functions to create unique resource name for each row in the calendar
export const generateBaseResourceId = (rateId, people) => `${rateId}-${people}-base`;
export const generatePeopleResourceId = (rateId, people) => `${rateId}-${people}-people`;
export const generateCheckinResourceId = (rateId) => `${rateId}-checkin`;
export const generateCheckoutResourceId = (rateId) => `${rateId}-checkout`;
export const generateMinPeopleResourceId = (rateId) => `${rateId}-minPeople`;
export const generateMinNightsResourceId = (rateId) => `${rateId}-minNights`;
export const generateEnabledResourceId = (rateId) => `${rateId}-enabled`;

export const extractRateId = (resourceId) => resourceId.split("-")[0];
export const extractPeople = (resourceId) => resourceId.split("-")[1];
export const isBaseResource = (resourceId) => resourceId.split("-")[2] === "base";
export const isPeopleResource = (resourceId) => resourceId.split("-")[2] === "people";
export const isCheckinResource = (resourceId) => resourceId.split("-")[1] === "checkin";
export const isCheckoutResource = (resourceId) => resourceId.split("-")[1] === "checkout";
export const isMinPeopleResource = (resourceId) => resourceId.split("-")[1] === "minPeople";
export const isMinNightsResource = (resourceId) => resourceId.split("-")[1] === "minNights";
export const isEnableResource = (resourceId) => resourceId.split("-")[1] === "enabled";

/*
 * Return an array with all the resources in the format expected by the planner.
 */
export const getResources = (rates, expanded, handleExpandClick, ratesViewMode) => {
    return rates
        .filter(rate => rate.isBase)  // TODO return the data for all the rates
        .flatMap((rate) => [
            {
                resourceId: generateBaseResourceId(rate.id, rate.basePeople),
                renderLabel: () => (
                    <Stack direction="row" spacing={1}>
                        <Stack direction="column">
                            <b>{rate.name} </b>
                            <PeopleTag peopleNumber={rate.basePeople} iconSize="10px"/>
                        </Stack>
                        <ExpandMore expand={expanded}
                                    onClick={handleExpandClick}
                                    aria-expanded={expanded}
                                    aria-label="show more">
                            <ExpandMoreIcon size="small"/>
                        </ExpandMore>
                    </Stack>
                ),
                renderDayCell: (date, gridData) => (!!gridData?.[date]?.pricesCent?.[rate.basePeople]) && (
                    <PriceTag><span>€</span>{formatPriceCent(gridData[date].pricesCent[rate.basePeople], rate.basePeople, ratesViewMode)}</PriceTag>
                )
            },
            ...[...Array(rate.maxPeople - rate.minPeople + 1).keys()]
                .map(i => rate.maxPeople - i)
                .filter(people => people !== rate.basePeople)
                .map(people => ({
                    resourceId: generatePeopleResourceId(rate.id, people),
                    renderLabel: () => <PeopleTag peopleNumber={people} iconSize="10px"/>,
                    renderDayCell: (date, gridData) => {
                        const data = gridData?.[date];
                        if (!data) {
                            return null;
                        }
                        if (data.pricesCent?.[people] === null || data.pricesCent?.[people] === undefined) {
                            return null;
                        }
                        if (data.minPeople !== null && people < data.minPeople) {
                            return null;
                        }
                        return formatPriceCent(data.pricesCent[people], people, ratesViewMode).toString();
                    },
                    hidden: !expanded,
                    size: "small"
                })),
            {
                resourceId: generateCheckinResourceId(rate.id),
                renderLabel: () => <Typography variant="body2">Arrivi</Typography>,
                renderDayCell: (date, gridData) => (gridData?.[date]?.checkin === true ?
                    <CheckCircleOutlineIcon fontSize="small" color="success"/> : ""),
                hidden: !expanded,
                size: "small"
            },
            {
                resourceId: generateCheckoutResourceId(rate.id),
                renderLabel: () => <Typography variant="body2">Partenze</Typography>,
                renderDayCell: (date, gridData) => (gridData?.[date]?.checkout === true ?
                    <CheckCircleOutlineIcon fontSize="small" color="success"/> : ""),
                hidden: !expanded,
                size: "small"
            },
            {
                resourceId: generateMinPeopleResourceId(rate.id),
                renderLabel: () => <Typography variant="body2">Minimo persone</Typography>,
                renderDayCell: (date, gridData) => (<>{gridData?.[date]?.minPeople !== null && gridData?.[date]?.minPeople}</>),
                hidden: !expanded,
                size: "small"
            },
            {
                resourceId: generateMinNightsResourceId(rate.id),
                renderLabel: () => <Typography variant="body2">Minimo Notti</Typography>,
                renderDayCell: (date, gridData) => (<>{gridData?.[date]?.minNights !== null && gridData?.[date]?.minNights}</>),
                hidden: !expanded,
                size: "small"
            },
            {
                resourceId: generateEnabledResourceId(rate.id),
                renderLabel: () => <Typography variant="body2">Stato</Typography>,
                renderDayCell: (date, gridData) => {
                    const status = getStatus(rate, gridData?.[date]);
                    if (status === "open") {
                        return <StatusTag><CircleCssIcon enabled/></StatusTag>;
                    }else if (status === "closed") {
                        return <StatusTag enabled={false}><CircleCssIcon enabled={false}/></StatusTag>;
                    }else if (status === "warning") {
                        return <StatusTag><WarningIcon fontSize="small" color="warning"/></StatusTag>;
                    }
                    return null;
                },
                size: "small"
            }])
}

const getStatus = (rate, gridData) => {
    // if grid data is not an object return null
    if (typeof gridData !== "object" || gridData === null || gridData === undefined) {
        return null;
    }
    if (gridData?.enabled === false) {
        return "closed";
    }

    const defined = (value) => value !== null && value !== undefined;
    const checkinIsDefined = defined(gridData.checkin);
    const checkoutIsDefined = defined(gridData.checkout);
    const minPeopleIsDefined = defined(gridData.minPeople);
    const minNightsIsDefined = defined(gridData.minNights);
    const enabledIsDefined = defined(gridData.enabled);

    let pricesCentIsDefined = false;
    if(minPeopleIsDefined && defined(rate?.maxPeople) && defined(rate?.minPeople)) {
        const minPeople = Math.max(rate.minPeople, gridData.minPeople);
        const numberPrices = rate.maxPeople - minPeople + 1;
        pricesCentIsDefined = Object.entries(gridData.pricesCent).filter(([people, value]) => people >= minPeople && defined(value)).length === numberPrices;
    }
    const allDefined = checkinIsDefined && checkoutIsDefined && minPeopleIsDefined && minNightsIsDefined && enabledIsDefined && pricesCentIsDefined;
    const allUndefined = !checkinIsDefined && !checkoutIsDefined && !minPeopleIsDefined && !minNightsIsDefined && !enabledIsDefined && !pricesCentIsDefined;

    if (allDefined) {
        return "open";
    }
    if (allUndefined) {
        // No data to show
        return null;
    }
    // Something is defined but not all, show warning
    return "warning"
}

const ExpandMore = styled((props) => {
    const {expand, ...other} = props;
    return <IconButton {...other} />;
})(({theme, expand}) => ({
    transform: !expand ? 'rotate(0deg)' : 'rotate(180deg)',
    marginLeft: 'auto',
    transition: theme.transitions.create('transform', {
        duration: theme.transitions.duration.shortest,
    }),
}));